/**
 * La tastiera a schermo, pensata per gli indirizzi.
 *
 * «Chi ha il telecomando non ha una tastiera.» Il disegno è quello del prototipo v3
 * (03-SCHERMATE-TV §3): una griglia a dieci colonne, tasti alti 64 px, un tasto largo
 * doppio `https://`, le lettere, le cifre, e i tasti utili — `.` `/` `:` `-` `_` `?` `=`
 * `&` `.it` `.com`.
 *
 * Chi ha una tastiera vera la usa e ignora tutto questo: i caratteri arrivano al campo
 * senza passare di qui (03-SCHERMATE-TV §1, terza eccezione).
 *
 * **Nessuna etichetta è tradotta**, e non è una dimenticanza: sono caratteri, non parole.
 * Le uniche due etichette che sono parole — cancella e svuota — arrivano dall'i18n.
 */

import type { ReactNode } from 'react';
import { colors } from '../theme.js';

export interface KeyboardKey {
  readonly label: string;
  /** Cosa scrive nel campo. Uguale all'etichetta tranne per i tasti larghi. */
  readonly text: string;
  readonly span: number;
}

const LETTERS = 'abcdefghijklmnopqrstuvwxyz'.split('');
const DIGITS = '0123456789'.split('');
const SYMBOLS = ['.', '/', ':', '-', '_', '?', '=', '&'];

export const ADDRESS_KEYS: ReadonlyArray<KeyboardKey> = [
  { label: 'https://', text: 'https://', span: 2 },
  { label: 'http://', text: 'http://', span: 2 },
  ...LETTERS.map((letter) => ({ label: letter, text: letter, span: 1 })),
  ...DIGITS.map((digit) => ({ label: digit, text: digit, span: 1 })),
  ...SYMBOLS.map((symbol) => ({ label: symbol, text: symbol, span: 1 })),
  { label: '.it', text: '.it', span: 1 },
  { label: '.com', text: '.com', span: 2 },
];

/** Per token, utente e password: lettere maiuscole comprese, niente scorciatoie di host. */
export const TEXT_KEYS: ReadonlyArray<KeyboardKey> = [
  ...LETTERS.map((letter) => ({ label: letter, text: letter, span: 1 })),
  ...LETTERS.map((letter) => ({ label: letter.toUpperCase(), text: letter.toUpperCase(), span: 1 })),
  ...DIGITS.map((digit) => ({ label: digit, text: digit, span: 1 })),
  ...['.', '-', '_', '@', '!', '#', '$', '%'].map((symbol) => ({ label: symbol, text: symbol, span: 1 })),
];

export interface OnScreenKeyboardProps {
  readonly keys: ReadonlyArray<KeyboardKey>;
  readonly onType: (text: string) => void;
  /** Reso `focusable` con `data-f`, come nel prototipo: è l'attributo su cui la
   *  navigazione spaziale raccoglie i candidati. */
  readonly disabled?: boolean;
}

export function OnScreenKeyboard({ keys, onType, disabled }: OnScreenKeyboardProps): ReactNode {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, minmax(0,1fr))', gap: 8 }}>
      {keys.map((key, index) => (
        <button
          key={`${key.label}-${index}`}
          type="button"
          data-key={key.text}
          data-f="1"
          disabled={disabled}
          onClick={() => onType(key.text)}
          style={{
            gridColumn: `span ${key.span}`,
            height: 64,
            border: 0,
            margin: 0,
            outline: 'none',
            cursor: 'pointer',
            borderRadius: 10,
            background: colors.surfaceRaised,
            color: colors.text,
            font: '600 24px ui-monospace, Menlo, monospace',
            transition: 'var(--tr, transform .16s ease)',
          }}
        >
          {key.label}
        </button>
      ))}
    </div>
  );
}
